// Interpretation-band helpers shared by the disease-activity calculators.
//
// A band list is ordered from lowest to highest score. Each band matches when
// the score is below its cutoff:
//   { lt: n }   -> matches when score <  n   (strict, e.g. DAS28 remission < 2.6)
//   { lte: n }  -> matches when score <= n   (inclusive, e.g. low disease <= 3.2)
//   (neither)   -> catch-all for the highest band
// Colors are reused across calculators so the same severity reads the same way.

export const BAND_COLORS = {
    remission: "#2e9e5b", // green
    inactive: "#2e9e5b",
    low: "#8bc34a", // light green
    moderate: "#e8a13a", // amber
    high: "#e05c4b", // red
    veryHigh: "#b23b2e", // dark red
    active: "#e05c4b",
    neutral: "#7a7a7a",
};

// Returns the first matching band, or a fallback if the list is empty.
export const findBand = (score, bands = []) => {
    for (const band of bands) {
        if (band.lt != null) {
            if (score < band.lt) return band;
        } else if (band.lte != null) {
            if (score <= band.lte) return band;
        } else {
            return band; // catch-all
        }
    }
    return { label: "—", color: BAND_COLORS.neutral };
};

// Builds a horizontal severity meter from a band list. Each band becomes a
// proportional segment (the open-ended top band borrows the previous width),
// and the marker is positioned along the full scale for the given score.
// Returns { segments: [{label,color,widthPct}], markerPct } — markerPct null if
// score is not a finite number.
export const buildMeter = (score, bands = []) => {
    const widths = [];
    let prev = 0;
    bands.forEach((b, i) => {
        const cutoff = b.lt != null ? b.lt : b.lte != null ? b.lte : null;
        if (cutoff == null) {
            const w = widths.length ? widths[widths.length - 1] : prev || 1;
            widths.push(w);
            prev += w;
        } else {
            widths.push(Math.max(cutoff - prev, 0.0001));
            prev = cutoff;
        }
    });
    const total = prev || 1;
    const segments = bands.map((b, i) => ({
        label: b.label,
        color: b.color,
        widthPct: (widths[i] / total) * 100,
    }));
    const markerPct =
        typeof score === "number" && !Number.isNaN(score)
            ? Math.max(0, Math.min(100, (score / total) * 100))
            : null;
    return { segments, markerPct };
};
